import { useMemo } from 'react'
import { useNetworkColumn } from '../hooks/useNetworkColumn'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { Advertiser } from '@/services/advertisers/advertisersApi'
import { getLocale } from '../utils/locale'
import { formatNumber } from '@/utils/format'

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  bidInLastDay: 'bidInLastDay',
  objectState: 'objectState',
  advertiserType: 'type',
  defaultCurrency: 'defaultCurrency',
  legalName: 'legalName',
  contact: 'contact',
  email: 'email',
  phone: 'phone',
  advertiserUrl: 'url',
  description: 'description',
  createTime: 'createTime',
}

interface AdvertiserTableProps {
  advertisers: Advertiser[]
  loading?: boolean
  emptyMessage?: string
  selection?: Advertiser[]
  onSelectionChange?: (advertisers: Advertiser[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function AdvertiserTable({
  advertisers,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: AdvertiserTableProps) {
  const { networkColumns, onRowClick } = useNetworkColumn<Advertiser>((row) => `/advertising/advertisers/${row.id}`)
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns = useMemo<ColumnDef<Advertiser, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<Advertiser>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    ...networkColumns,
    {
      accessorKey: 'ownerTeamName',
      header: t('common.team'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.ownerTeamName || '—'}
        </span>
      ),
    },
    {
      id: 'impressions24h',
      header: t('advertisers.impressions24h'),
      accessorFn: (row) => row.last24HourDeliveryStats?.impressionDelivery ?? 0,
      size: 150,
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.original.last24HourDeliveryStats
            ? formatNumber(row.original.last24HourDeliveryStats.impressionDelivery)
            : '—'}
        </span>
      ),
    },
    {
      id: 'spend24h',
      header: t('advertisers.spend24h'),
      accessorFn: (row) => row.last24HourDeliveryStats?.spendDelivery?.amount ?? 0,
      size: 130,
      cell: ({ row }) => {
        const s = row.original.last24HourDeliveryStats?.spendDelivery
        return (
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {s && s.amount > 0 ? formatMoney(s.amount, s.currency) : '—'}
          </span>
        )
      },
    },
    {
      id: 'impressions7d',
      header: t('advertisers.impressions7d'),
      accessorFn: (row) => row.last7DayDeliveryStats?.impressionDelivery ?? 0,
      size: 150,
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.original.last7DayDeliveryStats
            ? formatNumber(row.original.last7DayDeliveryStats.impressionDelivery)
            : '—'}
        </span>
      ),
    },
    {
      id: 'spend7d',
      header: t('advertisers.spend7d'),
      accessorFn: (row) => row.last7DayDeliveryStats?.spendDelivery?.amount ?? 0,
      size: 130,
      cell: ({ row }) => {
        const s = row.original.last7DayDeliveryStats?.spendDelivery
        return (
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {s && s.amount > 0 ? formatMoney(s.amount, s.currency) : '—'}
          </span>
        )
      },
    },
    {
      accessorKey: 'bidInLastDay',
      header: t('common.active'),
      size: 100,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.bidInLastDay ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.bidInLastDay ? t('advertisers.bidding') : t('advertisers.idle')}
        </span>
      ),
    },
    optionalColumn<Advertiser>({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'advertiserType',
      header: t('advertisers.entityType'),
      size: 130,
      cell: ({ row }) => <MutedCell value={row.original.advertiserType} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'defaultCurrency',
      header: t('common.currency'),
      size: 110,
      cell: ({ row }) => <MutedCell value={row.original.defaultCurrency} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'legalName',
      header: t('advertisers.legalName'),
      meta: { minWidth: 160 },
      cell: ({ row }) => <MutedCell value={row.original.legalName} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'contact',
      header: t('advertisers.contact'),
      meta: { minWidth: 140 },
      cell: ({ row }) => <MutedCell value={row.original.contact} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'email',
      header: t('common.email'),
      meta: { minWidth: 160 },
      cell: ({ row }) => <MutedCell value={row.original.email} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'phone',
      header: t('common.phone'),
      size: 130,
      cell: ({ row }) => <MutedCell value={row.original.phone} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'advertiserUrl',
      header: t('common.website'),
      meta: { minWidth: 160 },
      cell: ({ row }) => <MutedCell value={row.original.advertiserUrl} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'description',
      header: t('common.description'),
      meta: { minWidth: 180 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<Advertiser>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<Advertiser>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ], [t, selectable, networkColumns])

  return (
    <DataTable
      columns={columns}
      data={advertisers}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No advertisers found.'}
      columnsKey="advertisers"
      onRowClick={onRowClick}
      selection={selection}
      onSelectionChange={onSelectionChange}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
